import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { MorningTodayWidget } from '../components/brand/MorningTodayWidget';
import { BentoGrid, BentoTile, Counter, TileHeader } from '../components/brand/BentoWidgets';
import {
  LayoutDashboard, Search, Handshake, MessageSquare, Building2,
  Users, CheckCircle2, Clock, Loader2, Check, ArrowRight, Briefcase, MapPin, ShieldCheck,
  LayoutGrid,
} from 'lucide-react';import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TwoFactorAuth } from '../components/brand/TwoFactorAuth';
import { ProfilePhotoUpload } from '../components/brand/ProfilePhotoUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AgencyDashboard() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ open_clinicians: 0, engagements_sent: 0, engagements_accepted: 0, engagements_pending: 0 });
  const [orgTypes, setOrgTypes] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    org_name: '', org_type: '', website: '', description: '', city: '', state: '', contact_name: '', contact_phone: '', photo_url: '',
  });

  useEffect(() => {
    axios.get(`${API}/agency/stats`, getAuthHeader()).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/agency/meta`).then((r) => setOrgTypes(r.data.org_types || [])).catch(() => {});
    axios.get(`${API}/engagements`, getAuthHeader()).then((r) => setEngagements(r.data || [])).catch(() => {});
    axios.get(`${API}/agency/profile`, getAuthHeader()).then((r) => {
      const p = r.data || {};
      setProfile({
        org_name: p.org_name || '', org_type: p.org_type || '', website: p.website || '',
        description: p.description || '', city: p.city || '', state: p.state || '',
        contact_name: p.contact_name || '', contact_phone: p.contact_phone || '', photo_url: p.photo_url || '',
      });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/agency/profile`, profile, getAuthHeader());
      toast.success('Organization profile saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save profile');
    } finally { setSaving(false); }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', active: true },
    { icon: LayoutGrid, label: 'Executive Dashboard', href: '/operations/executive' },
    { icon: Search, label: 'Find clinicians', href: '/agency/find-clinicians' },
    { icon: Handshake, label: 'Engagements', href: '#engagements' },
    { icon: Users, label: 'Team', href: '/org/team' },
    { icon: MessageSquare, label: 'Messages', href: '/agency/messages' },
    { icon: Building2, label: 'Organization', href: '#profile' },
    { icon: ShieldCheck, label: 'Account security', href: '#security' },
  ];

  const statusBadge = (s) => {
    const map = {
      pending: 'bg-cn-warning/10 text-cn-warning border-cn-warning/25',
      accepted: 'bg-cn-success/10 text-cn-success border-cn-success/25',
      declined: 'bg-cn-border/40 text-cn-text-muted border-cn-border',
    };
    return <Badge className={`border ${map[s] || map.pending} capitalize`}>{s}</Badge>;
  };

  const STAT_TILES = [
    { label: 'Clinicians available', value: stats.open_clinicians, icon: Users },
    { label: 'Requests sent', value: stats.engagements_sent, icon: Handshake },
    { label: 'Accepted', value: stats.engagements_accepted, icon: CheckCircle2 },
    { label: 'Pending', value: stats.engagements_pending, icon: Clock },
  ];

  return (
    <DashboardShell
      navItems={navItems}
      title={`Welcome, ${profile.org_name || user?.first_name || 'there'}`}
      subtitle="Your workforce, operations, compliance, and revenue cycle — in one workspace."
      rightSlot={
        <Link to="/agency/find-clinicians" className="cn-btn cn-btn-primary text-sm" data-testid="agency-find-cta">
          <Search className="w-4 h-4" /> Find clinicians
        </Link>
      }
      testId="agency-dashboard"
    >
      <div className="mb-5">
        <MorningTodayWidget getAuthHeader={getAuthHeader} />
      </div>

      <BentoGrid>
        {/* Stats */}
        {STAT_TILES.map((s) => (
          <BentoTile key={s.label} span="col-span-1 md:col-span-2 lg:col-span-3" className="p-5" testId={`agency-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center"><s.icon className="w-5 h-5" /></div>
            </div>
            <div className="mt-4 cn-display text-4xl text-cn-text dark:text-white"><Counter value={s.value} /></div>
            <div className="text-sm text-cn-text-muted mt-1">{s.label}</div>
          </BentoTile>
        ))}

        {/* Find clinicians CTA */}
        <BentoTile span="col-span-full lg:col-span-5" className="p-7 !bg-cn-primary text-white" hover={false}>
          <p className="cn-eyebrow !text-white/70">Staffing</p>
          <h3 className="cn-display text-2xl mt-1 !text-white">Build your bench, fast.</h3>
          <p className="text-white/80 text-sm mt-2 leading-relaxed">
            Browse verified, license-monitored clinicians who&apos;ve opted into agency work, then send a temporary or permanent engagement request in one click.
          </p>
          <Link to="/agency/find-clinicians" className="cn-btn cn-btn-accent mt-5 text-sm" data-testid="agency-browse-cta">
            Browse clinicians <ArrowRight className="w-4 h-4" />
          </Link>
        </BentoTile>

        {/* Organization profile */}
        <BentoTile span="col-span-full lg:col-span-7" className="p-6 sm:p-7" id="profile" testId="agency-profile-tile">
          <TileHeader eyebrow="Organization" title="Your organization profile" />
          <p className="text-sm text-cn-text-muted mt-2">Clinicians see this when you reach out. A complete profile builds trust.</p>
          <div className="mt-5">
            <ProfilePhotoUpload
              currentUrl={profile.photo_url}
              onUploaded={(url) => setProfile((p) => ({ ...p, photo_url: url }))}
              label="Organization logo"
              hint="Shown to clinicians when you send an engagement request."
              shape="square"
              testId="agency-logo-upload"
            />
          </div>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Organization name</Label>
              <Input value={profile.org_name} onChange={(e) => set('org_name', e.target.value)} placeholder="Acme Staffing" className="mt-1.5 h-11 rounded-xl border-cn-border" data-testid="agency-org-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Organization type</Label>
              <Select value={profile.org_type} onValueChange={(v) => set('org_type', v)}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl border-cn-border" data-testid="agency-org-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {orgTypes.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">City</Label>
              <Input value={profile.city} onChange={(e) => set('city', e.target.value)} placeholder="Austin" className="mt-1.5 h-11 rounded-xl border-cn-border" data-testid="agency-city" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">State</Label>
              <Input value={profile.state} onChange={(e) => set('state', e.target.value)} maxLength={2} placeholder="TX" className="mt-1.5 h-11 rounded-xl border-cn-border uppercase" data-testid="agency-state" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Contact name</Label>
              <Input value={profile.contact_name} onChange={(e) => set('contact_name', e.target.value)} placeholder="Pat Lee" className="mt-1.5 h-11 rounded-xl border-cn-border" data-testid="agency-contact-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Contact phone</Label>
              <Input value={profile.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} placeholder="(555) 123-4567" className="mt-1.5 h-11 rounded-xl border-cn-border" data-testid="agency-contact-phone" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Website</Label>
              <Input value={profile.website} onChange={(e) => set('website', e.target.value)} placeholder="https://acmestaffing.com" className="mt-1.5 h-11 rounded-xl border-cn-border" data-testid="agency-website" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">About your organization</Label>
              <Textarea value={profile.description} onChange={(e) => set('description', e.target.value)} placeholder="Who you are, what you staff, and what makes you a great partner." className="mt-1.5 min-h-[80px] rounded-xl border-cn-border" data-testid="agency-description" />
            </div>
          </div>
          <Button onClick={saveProfile} disabled={saving} className="cn-btn cn-btn-primary w-full sm:w-auto mt-5 !py-2.5" data-testid="agency-save-profile">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {saving ? 'Saving…' : 'Save organization profile'}
          </Button>
        </BentoTile>

        {/* Engagements */}
        <BentoTile span="col-span-full" className="p-6 sm:p-7" id="engagements" testId="agency-engagements-tile">
          <TileHeader eyebrow="Pipeline" title="Engagement requests" />
          {engagements.length === 0 ? (
            <p className="mt-5 text-sm text-cn-text-muted">No engagement requests yet. <Link to="/agency/find-clinicians" className="text-cn-primary font-semibold hover:underline">Find clinicians</Link> to send your first one.</p>
          ) : (
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              {engagements.map((e) => (
                <div key={e.id} className="rounded-2xl border border-cn-border/70 p-4 bg-cn-bg/40" data-testid={`agency-engagement-${e.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-cn-primary bg-cn-sage-100 px-2 py-0.5 rounded-full capitalize">
                      {e.engagement_type === 'permanent' ? <Briefcase className="w-3 h-3" /> : <Clock className="w-3 h-3" />}{e.engagement_type}
                    </span>
                    {statusBadge(e.status)}
                  </div>
                  <h4 className="font-display font-semibold text-cn-text mt-2 leading-snug">{e.title}</h4>
                  <p className="text-xs text-cn-text-muted mt-0.5 flex items-center gap-1"><Users className="w-3 h-3" /> {e.clinician_name}{e.location ? <> · <MapPin className="w-3 h-3" /> {e.location}</> : null}</p>
                  {(e.duration || e.rate) && <p className="text-xs text-cn-text-muted mt-1">{[e.duration, e.rate].filter(Boolean).join(' · ')}</p>}
                  {e.status === 'accepted' && e.response_message && (
                    <p className="text-sm text-cn-text mt-2 p-2 rounded-xl bg-cn-success/8 border border-cn-success/20">{e.response_message}</p>
                  )}
                  {e.status === 'accepted' && (
                    <button
                      onClick={() => navigate(`/agency/messages?with=${e.clinician_id}&name=${encodeURIComponent(e.clinician_name || '')}`)}
                      className="cn-btn cn-btn-primary text-xs mt-3 !py-1.5"
                      data-testid={`agency-message-${e.id}`}
                    >
                      <MessageSquare className="w-3.5 h-3.5" /> Message {e.clinician_name?.split(' ')[0] || 'clinician'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </BentoTile>

        {/* Account security */}
        <div id="security" className="col-span-full scroll-mt-24" data-testid="agency-security-tile">
          <div className="mb-3">
            <p className="cn-eyebrow">Account security</p>
            <h3 className="cn-display text-2xl text-cn-text dark:text-white">Protect your organization account</h3>
          </div>
          <TwoFactorAuth />
        </div>
      </BentoGrid>
    </DashboardShell>
  );
}
